import {
  ChangeDetectionStrategy, Component, EventEmitter, HostListener,
  Input, OnInit, Output, computed, signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AbilitiesService } from '../../services/abilities.service';

interface TimeframeOption { id: string; label: string; }

/**
 * Request-an-ability dialog — the form behind a catalog's no-match "Request …"
 * flow (the Abilities modal and the full /abilities page). Mirrors Studio's
 * app-request-ability-dialog field-for-field (same title, copy, validation and
 * two-state form → sent flow) so the experience is identical whether the
 * request starts in Hub or in Studio. Posts through the SAME
 * agenthome/abilities/requests contract the sibling "Request access" dialog
 * uses (AbilitiesService.requestAbility) — only the form in front of it differs.
 */
@Component({
  selector: 'app-request-ability-dialog',
  standalone: true,
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './request-ability-dialog.component.html',
  styleUrls: ['./request-ability-dialog.component.scss'],
})
export class RequestAbilityDialogComponent implements OnInit {
  /** Pre-filled from the search box that found nothing. */
  @Input() term = '';
  /** Where the request originated, e.g. "hub-search" | "hub-abilities-modal". */
  @Input() source = 'hub-search';

  @Output() readonly close = new EventEmitter<void>();
  /** Emits the (lowercased) requested term on success so the host can flip its "Requested" chip. */
  @Output() readonly submitted = new EventEmitter<string>();

  readonly timeframes: TimeframeOption[] = [
    { id: 'asap', label: 'As soon as possible' },
    { id: 'month', label: 'Within a month' },
    { id: 'quarter', label: 'This quarter' },
    { id: 'exploring', label: 'Just exploring' },
  ];

  readonly what = signal('');
  readonly useCase = signal('');
  readonly timeframe = signal('exploring');
  readonly submitting = signal(false);
  readonly sent = signal(false);
  readonly error = signal('');

  /** The use case is the point of the form — a bare name isn't actionable. */
  readonly canSubmit = computed(() =>
    this.what().trim().length > 0 && this.useCase().trim().length >= 10 && !this.submitting());

  constructor(private readonly abilitiesSvc: AbilitiesService) {}

  ngOnInit(): void {
    this.what.set(this.term.trim());
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.submitting()) this.onClose();
  }

  onClose(): void {
    this.close.emit();
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.submitting.set(true);
    this.error.set('');
    const term = this.what().trim();
    this.abilitiesSvc.requestAbility({
      term,
      source: this.source,
      use_case: this.useCase().trim(),
      timeframe: this.timeframe(),
    }).subscribe(ok => {
      this.submitting.set(false);
      if (ok) {
        this.sent.set(true);
        this.submitted.emit(term.toLowerCase());
      } else {
        this.error.set('Something went wrong sending your request. Please try again.');
      }
    });
  }
}
